/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import { always, Maybe, NonEmptyList } from 'purify-ts';
import Vue from 'vue';
import Router from 'vue-router';
import { object, registerArray, sort } from '@codegrade/utils';
import { lazyLoadView } from '@/components/higher_order/LazyLoader';
import { UserStore } from '@/store';
import * as utils from '@/utils';
Vue.use(Router);
const NO_STUDENT_VIEW = always(Maybe.empty());
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class MyRouter extends Router {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _previousRoute = null;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    navigationCheckers = new registerArray.RegisterArray();
    /** The amount of time the title of the page has changed, only used in DEV mode. */
    _titleChanges = 0;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(options) {
        super(options);
        this.beforeEach(async (to, from, next) => {
            for (const navigationChecker of this.navigationCheckers) {
                const stopNext = navigationChecker(to, from);
                if (stopNext) {
                    next(false);
                    return;
                }
            }
            const loggedIn = UserStore.loggedIn();
            const routeNameChanged = to.name !== from.name;
            if (!loggedIn && to.meta?.loginRequired === true) {
                this.logoutAndRedirectAfterLogin(to.fullPath);
                // Show toast notification only on the first page load.
                if (this._previousRoute == null) {
                    const app = this.app;
                    app.$nextTick(() => {
                        app.notLoggedInError('You are currently not logged in. Please log in to view this page.');
                    });
                }
            }
            else {
                next();
                /*
                 * In non-production environments we check that for every route change
                 * there is a corresponding title change within a set timeout.
                 * This makes cypress test fail when this is not the case.
                 * This check also enforces that for each page, the title is set
                 * to something different than `CodeGrade`.
                 *
                 * This check needs to happen only when there is no redirect.
                 * otherwise it will look for an extra name change.
                 * example: I am navigating to Home but i am not logged in:
                 * Title goes Opening -> Login but beforeEach has been called 3
                 * Times [null, Home, Login]. Since home is never actually loaded
                 * the extra title is not ever there.
                 */
                if (!IS_PRODUCTION && routeNameChanged) {
                    const oldAmount = this._titleChanges;
                    const newRoute = to.name ?? 'null';
                    const oldRoute = from.name ?? 'null';
                    setTimeout(() => {
                        if (oldAmount >= this._titleChanges) {
                            throw new Error(`No title change happened from ${oldRoute} to ${newRoute}` +
                                ` ${oldAmount}, ${this._titleChanges}`);
                        }
                    }, 3_500);
                }
            }
            if (from.name != null && routeNameChanged) {
                this._previousRoute = { ...from };
            }
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    addNavigationChecker(checker) {
        return this.navigationCheckers.add(checker);
    }
    /**
     * Set the page title to the new name.
     *
     * @param name The name of the new title.
     */
    setPageTitle(name) {
        this._titleChanges += 1;
        document.title = `${name} - CodeGrade`;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    logoutAndRedirectAfterLogin(redirect) {
        const nextRoute = redirect ?? this.currentRoute.fullPath;
        UserStore.logout({ revoke: false }).catch(err => {
            // eslint-disable-next-line no-console
            console.error(err, 'Failed to log out');
        });
        this.push({
            name: 'login',
            query: {
                redirect: nextRoute,
            },
        }).catch(err => {
            // eslint-disable-next-line no-console
            console.error(err, 'Failed to redirect');
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getPreviousRoute() {
        return this._previousRoute;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    safeReplace(url, defaultReplacement) {
        const didReplace = this.maybeReplace(url);
        if (!didReplace) {
            this.replace(defaultReplacement);
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    maybeReplace(url) {
        if (url == null) {
            return false;
        }
        else if (!utils.urlSameOrigin(url)) {
            return false;
        }
        else {
            this.replace(utils.stripURLOrigin(url));
            return true;
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getQuery(query) {
        const val = this.app.$route.query[query];
        if (val == null) {
            return Maybe.empty();
        }
        else if (Array.isArray(val)) {
            const arr = sort.sortBy(object.filterMap(val, Maybe.fromNullable), el => [el.length]);
            return NonEmptyList.fromArray(arr).map(x => x[0]);
        }
        else {
            return Maybe.of(val);
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    push(location) {
        let loc = location;
        if (typeof location === 'object' && location.name === null) {
            loc = { ...location, name: undefined };
        }
        return super.push(loc).catch(err => this._ignoreNavigationDuplicated(err));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    replace(location) {
        let loc = location;
        if (typeof location === 'object' && location.name === null) {
            loc = { ...location, name: undefined };
        }
        return super.replace(loc).catch(err => this._ignoreNavigationDuplicated(err));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _ignoreNavigationDuplicated(err) {
        // Newer versions of vue-router reject promises returned by push/replace
        // with a NavigationDuplicated error, even though nothing is actually
        // wrong, so ignore these errors.
        if (err.name === 'NavigationDuplicated') {
            return this.currentRoute;
        }
        throw err;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
const getDefault = (data) => data.default;
/**
 * Takes an array of route configuration objects and makes them immutable by freezing each one,
 * including their nested 'meta' properties.
 *
 * @param routes An array of route configuration objects.
 * @returns An array of the same route configuration objects, but each made immutable.
 */
function freezeRoutes(routes) {
    return routes.map(route => {
        Object.freeze(route.meta);
        return Object.freeze(route);
    });
}
const STUDENT_VIEW_SUBMISSIONS = 
/**
 * Get the student view for the current course to the submissions page.
 *
 * @param route The current route.
 * @returns A student view to the submissions page.
 */
route => Maybe.of({
    location: {
        name: 'assignment_submissions',
        params: {
            courseId: route.params.courseId,
            assignmentId: route.params.assignmentId,
        },
        hash: '#student-start',
        query: {},
    },
    courseId: parseInt(route.params.courseId, 10),
});
const router = new MyRouter({
    mode: 'history',
    routes: freezeRoutes([
        {
            path: '/',
            name: 'home',
            component: lazyLoadView({
                errorMessage: 'Could not load home page.',
                // @ts-expect-error This page is not yet in typescript.
                getComponent: () => import('@/pages/Home').then(getDefault),
            }),
            meta: {
                loginRequired: true,
                hideSidebar: false,
                hideFooter: false,
                floatingSidebar: true,
                initiallySidebarOpen: false,
                readableName: 'Home',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        {
            path: '/login/:tenantId?',
            name: 'login',
            component: lazyLoadView({
                getComponent: () => import('@/pages/Login').then(getDefault),
                errorMessage: 'Could not load login page.',
            }),
            meta: {
                loginRequired: false,
                hideSidebar: false,
                hideFooter: false,
                floatingSidebar: false,
                initiallySidebarOpen: false,
                readableName: 'Login',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        {
            path: '/forgot/:tenantId?',
            name: 'forgot',
            component: lazyLoadView({
                getComponent: () => import('@/pages/ForgotPassword').then(getDefault),
                errorMessage: 'Could not load forgot password page.',
            }),
            meta: {
                loginRequired: false,
                hideSidebar: false,
                hideFooter: false,
                floatingSidebar: true,
                initiallySidebarOpen: false,
                readableName: 'Forgot Password',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        {
            path: '/register/:tenantId?',
            name: 'register',
            component: lazyLoadView({
                getComponent: () => import('@/pages/Register').then(getDefault),
                errorMessage: 'Could not load register page.',
            }),
            meta: {
                loginRequired: false,
                hideSidebar: false,
                hideFooter: false,
                floatingSidebar: false,
                initiallySidebarOpen: false,
                readableName: 'Register',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        {
            path: '/tenants/:tenantId/users/:userId/initial_password',
            name: 'initial-password',
            component: lazyLoadView({
                errorMessage: 'Could not load initial password page.',
                getComponent: () => import('@/pages/SetInitialPassword').then(getDefault),
            }),
            meta: {
                loginRequired: false,
                hideSidebar: false,
                hideFooter: false,
                floatingSidebar: false,
                initiallySidebarOpen: false,
                readableName: 'Set Inital Password',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        {
            path: '/tenants/:tenantId/users/:userId/reset_password',
            name: 'reset-password-new',
            component: lazyLoadView({
                errorMessage: 'Could not load reset password page.',
                getComponent: () => import('@/pages/ResetPassword').then(getDefault),
            }),
            meta: {
                loginRequired: false,
                hideSidebar: false,
                hideFooter: false,
                floatingSidebar: false,
                initiallySidebarOpen: false,
                readableName: 'Reset Password',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        // This route is deprecated and redirects to `reset-password-new`
        {
            path: '/reset_password',
            name: 'reset-password',
            component: lazyLoadView({
                errorMessage: 'Could not load reset password page.',
                getComponent: () => import('@/pages/ResetPassword').then(getDefault),
            }),
            meta: {
                loginRequired: false,
                hideSidebar: false,
                hideFooter: false,
                floatingSidebar: false,
                initiallySidebarOpen: false,
                readableName: 'Reset Password',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        {
            path: '/lti_launch/',
            name: 'lti-launch',
            component: lazyLoadView({
                // @ts-expect-error This component is not yet in typescript.
                getComponent: () => import('@/pages/LTILaunch').then(getDefault),
                errorMessage: 'Could not load LTI launch page.',
            }),
            meta: {
                loginRequired: false,
                hideSidebar: true,
                floatingSidebar: false,
                initiallySidebarOpen: true,
                hideFooter: false,
                readableName: 'LTI Launch',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        {
            path: '/admin',
            name: 'admin',
            component: lazyLoadView({
                errorMessage: 'Could not load admin page.',
                getComponent: () => import('@/pages/Admin').then(getDefault),
            }),
            meta: {
                loginRequired: true,
                hideSidebar: false,
                hideFooter: false,
                floatingSidebar: false,
                initiallySidebarOpen: true,
                readableName: 'Administration',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        {
            path: '/courses/:courseId/assignments/:assignmentId/submissions/:submissionId',
            name: 'submission',
            redirect: (to) => ({
                ...to,
                name: 'submission_file',
            }),
            meta: {
                loginRequired: true,
                hideSidebar: false,
                hideFooter: true,
                floatingSidebar: true,
                initiallySidebarOpen: false,
                readableName: 'Submission',
                getStudentViewPage: STUDENT_VIEW_SUBMISSIONS,
            },
        },
        {
            path: '/courses/:courseId/assignments/:assignmentId/editor/:editSubmissionId/',
            name: 'edit_page',
            component: lazyLoadView({
                errorMessage: 'Could not load the code editor.',
                getComponent: () => import('@/pages/CodeEditor').then(getDefault),
            }),
            meta: {
                loginRequired: true,
                hideSidebar: false,
                hideFooter: true,
                floatingSidebar: true,
                initiallySidebarOpen: false,
                readableName: 'Code Editor',
                getStudentViewPage: STUDENT_VIEW_SUBMISSIONS,
            },
        },
        {
            path: '/courses/:courseId/assignments/:assignmentId/submissions/:submissionId/files/:fileId?',
            name: 'submission_file',
            component: lazyLoadView({
                // @ts-expect-error: This component is not in typescript yet.
                getComponent: () => import('@/pages/Submission').then(getDefault),
                errorMessage: 'Could not load submission page.',
            }),
            meta: {
                loginRequired: true,
                hideSidebar: false,
                hideFooter: true,
                floatingSidebar: true,
                initiallySidebarOpen: false,
                readableName: 'Submission',
                getStudentViewPage: STUDENT_VIEW_SUBMISSIONS,
            },
        },
        {
            path: '/courses/:courseId/assignments/:assignmentId/submissions/',
            name: 'assignment_submissions',
            component: lazyLoadView({
                getComponent: () => import('@/pages/Submissions').then(getDefault),
                errorMessage: 'Could not load submissions page.',
            }),
            meta: {
                loginRequired: true,
                hideSidebar: false,
                hideFooter: false,
                floatingSidebar: false,
                initiallySidebarOpen: true,
                readableName: 'Submissions',
                getStudentViewPage: STUDENT_VIEW_SUBMISSIONS,
            },
        },
        {
            path: '/courses/:courseId',
            name: 'manage_course',
            component: lazyLoadView({
                errorMessage: 'Could not load manage course page.',
                getComponent: () => import('@/pages/ManageCourse').then(getDefault),
            }),
            meta: {
                loginRequired: true,
                hideSidebar: false,
                hideFooter: false,
                floatingSidebar: false,
                initiallySidebarOpen: true,
                readableName: 'Course Management',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        {
            path: '/courses/:courseId/assignments/:assignmentId',
            name: 'manage_assignment',
            component: lazyLoadView({
                errorMessage: 'Could not load manage assignment page.',
                // @ts-expect-error: This component is not in typescript yet.
                getComponent: () => import('@/pages/ManageAssignment').then(getDefault),
            }),
            meta: {
                loginRequired: true,
                hideSidebar: false,
                hideFooter: false,
                floatingSidebar: false,
                initiallySidebarOpen: true,
                readableName: 'Assignment Management',
                getStudentViewPage: STUDENT_VIEW_SUBMISSIONS,
            },
        },
        {
            path: '/courses/:courseId/group_sets/:groupSetId',
            name: 'manage_groups',
            component: lazyLoadView({
                // @ts-expect-error: This component is not in typescript yet.
                getComponent: () => import('@/pages/Groups').then(getDefault),
                errorMessage: 'Could not load groups page.',
            }),
            meta: {
                loginRequired: true,
                hideSidebar: false,
                hideFooter: false,
                floatingSidebar: false,
                initiallySidebarOpen: true,
                readableName: 'Groups',
                getStudentViewPage: route => Maybe.of({
                    location: route,
                    courseId: parseInt(route.params.courseId, 10),
                }),
            },
        },
        {
            path: '/courses/:courseId/assignments/:assignmentId/plagiarism/:plagiarismRunId/cases/',
            name: 'plagiarism_overview',
            component: lazyLoadView({
                errorMessage: 'Could not plagiarism overview.',
                // @ts-expect-error: This component is not in typescript yet.
                getComponent: () => import('@/components/PlagiarismOverview').then(getDefault),
            }),
            meta: {
                loginRequired: true,
                hideSidebar: false,
                hideFooter: false,
                floatingSidebar: true,
                initiallySidebarOpen: false,
                readableName: 'Plagiarism Overview',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        {
            path: '/courses/:courseId/assignments/:assignmentId/plagiarism/:plagiarismRunId/cases/:plagiarismCaseId',
            name: 'plagiarism_detail',
            component: lazyLoadView({
                errorMessage: 'Could not load plagiarism detail.',
                // @ts-expect-error: This component is not in typescript yet.
                getComponent: () => import('@/components/PlagiarismDetail').then(getDefault),
            }),
            meta: {
                loginRequired: true,
                hideSidebar: false,
                hideFooter: true,
                floatingSidebar: true,
                initiallySidebarOpen: false,
                readableName: 'Plagiarism Detail',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        {
            path: '/login_and_redirect/:loginFile',
            name: 'login_and_redirect',
            component: lazyLoadView({
                // @ts-expect-error: This component is not in typescript yet.
                getComponent: () => import('@/pages/LoginAndRedirect').then(getDefault),
                errorMessage: 'Could not load login and redirect page.',
            }),
            meta: {
                loginRequired: false,
                hideSidebar: false,
                hideFooter: false,
                floatingSidebar: false,
                initiallySidebarOpen: true,
                readableName: 'Login and Redirect',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        {
            path: '/unsubscribe/email_notifications/',
            name: 'unsubscribe',
            component: lazyLoadView({
                errorMessage: 'Could not load unsubscribe page.',
                // @ts-expect-error: This component is not in typescript yet.
                getComponent: () => import('@/pages/UnsubscribePage').then(getDefault),
            }),
            meta: {
                loginRequired: false,
                hideSidebar: true,
                floatingSidebar: false,
                initiallySidebarOpen: false,
                hideFooter: false,
                readableName: 'Unsubscribe from Email Notifications',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        {
            path: '/lti_providers/:ltiProviderId/setup',
            name: 'lti_provider_setup',
            component: lazyLoadView({
                getComponent: () => import('@/pages/LTIProviderSetup').then(getDefault),
                errorMessage: 'Could not load LTI provider setup page.',
            }),
            meta: {
                loginRequired: false,
                hideSidebar: true,
                floatingSidebar: false,
                initiallySidebarOpen: true,
                hideFooter: false,
                readableName: 'LTI Provider Setup',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        {
            path: '/lti_providers/:ltiProviderId',
            name: 'lti_provider',
            component: lazyLoadView({
                errorMessage: 'Could not load lti provider page.',
                getComponent: () => import('@/pages/LTIProvider').then(getDefault),
            }),
            meta: {
                loginRequired: true,
                hideSidebar: false,
                hideFooter: false,
                floatingSidebar: false,
                initiallySidebarOpen: true,
                readableName: 'LTI Provider',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        {
            path: '/assignments/:assignmentId/login/:loginUuid',
            name: 'assignment_login',
            component: lazyLoadView({
                // @ts-expect-error: This component is not in typescript yet.
                getComponent: () => import('@/pages/AssignmentLogin').then(getDefault),
                errorMessage: 'Could not load exam page.',
            }),
            meta: {
                loginRequired: false,
                hideSidebar: true,
                floatingSidebar: false,
                initiallySidebarOpen: true,
                hideFooter: false,
                readableName: 'Exam Page',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        {
            path: '/courses/:courseId/enroll/:linkId',
            name: 'course_enroll',
            component: lazyLoadView({
                errorMessage: 'Could not load course enroll page.',
                getComponent: () => import('@/pages/CourseEnroll').then(getDefault),
            }),
            meta: {
                loginRequired: false,
                hideSidebar: true,
                floatingSidebar: false,
                initiallySidebarOpen: true,
                hideFooter: false,
                readableName: 'Course Enrollment',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        {
            path: '/sso_login/:blobId',
            name: 'sso_login',
            component: lazyLoadView({
                getComponent: () => import('@/pages/SsoLogin').then(getDefault),
                errorMessage: 'Could not load SSO login page.',
            }),
            meta: {
                loginRequired: false,
                hideSidebar: true,
                floatingSidebar: false,
                initiallySidebarOpen: true,
                hideFooter: false,
                readableName: 'SSO Login',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        {
            path: '/tenant/:tenantId',
            name: 'tenant',
            component: lazyLoadView({
                errorMessage: 'Could not load the tenant page.',
                getComponent: () => import('@/pages/Tenant').then(getDefault),
            }),
            meta: {
                loginRequired: true,
                hideSidebar: false,
                hideFooter: false,
                floatingSidebar: false,
                initiallySidebarOpen: true,
                readableName: 'Tenant',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        {
            path: '/courses/:courseId/assignments/:assignmentId/snapshot/:configId',
            alias: '/courses/:courseId/assignments/:assignmentId/code_execution/:configId',
            name: 'manage_deploy_config',
            component: lazyLoadView({
                errorMessage: 'Could not load snapshot page.',
                getComponent: () => import('@/pages/NewAutoTest/Deploy').then(getDefault),
            }),
            meta: {
                loginRequired: true,
                hideSidebar: false,
                hideFooter: false,
                floatingSidebar: true,
                initiallySidebarOpen: false,
                readableName: 'AutoTest v2 Snapshot',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        {
            path: '/courses/:courseId/transactions/:transactionId',
            name: 'after_pay',
            component: lazyLoadView({
                errorMessage: 'Could not load after pay page.',
                getComponent: () => import('@/pages/AfterPay').then(getDefault),
            }),
            meta: {
                loginRequired: false,
                hideSidebar: true,
                floatingSidebar: false,
                initiallySidebarOpen: true,
                hideFooter: false,
                readableName: 'After Pay',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        {
            path: '/profile',
            name: 'user_profile',
            component: lazyLoadView({
                getComponent: () => import('@/pages/UserProfile').then(getDefault),
                errorMessage: 'Could not load user profile page.',
            }),
            meta: {
                loginRequired: true,
                hideSidebar: false,
                hideFooter: false,
                floatingSidebar: false,
                initiallySidebarOpen: true,
                readableName: 'User Profile',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
        {
            path: '/course/:courseId/sections/:sectionId',
            name: 'course_section',
            component: lazyLoadView({
                getComponent: () => import('@/pages/CourseSection').then(getDefault),
                errorMessage: 'Could not load course section page.',
            }),
            meta: {
                loginRequired: true,
                hideSidebar: false,
                hideFooter: false,
                floatingSidebar: false,
                initiallySidebarOpen: true,
                readableName: 'Course Section',
                getStudentViewPage: route => Maybe.of({
                    location: route,
                    courseId: parseInt(route.params.courseId, 10),
                }),
            },
        },
        {
            path: '/courses/:courseId/pearson-templates/:templateId/preview',
            name: 'pearson_template_preview',
            component: lazyLoadView({
                getComponent: () => import('@/pages/PearsonTemplatePreview').then(getDefault),
                errorMessage: 'Could not load pearson template preview page',
            }),
            meta: {
                loginRequired: true,
                hideSidebar: true,
                hideFooter: true,
                floatingSidebar: false,
                initiallySidebarOpen: false,
                readableName: 'Template preview',
                getStudentViewPage: NO_STUDENT_VIEW,
            },
        },
    ]),
});
export default router;
